import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  Inject,
  signal,
  viewChild,
} from '@angular/core';
import type { EditorEvents } from '@tiptap/core';
import { IconComponent } from '@examdojo/core/icon';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import { StopPropagationDirective } from '@examdojo/core/stop-propagation';
import { KatexOptions, MarkdownModule } from 'ngx-markdown';
import { TiptapNodeViewContentDirective } from 'ngx-tiptap';

interface RecipeStepAttrs {
  title?: string;
}

@Component({
  selector: 'dojo-recipe-step',
  standalone: true,
  imports: [TiptapNodeViewContentDirective, StopPropagationDirective, IconComponent, MarkdownModule],
  templateUrl: './recipe-step.component.html',
  styleUrls: ['./recipe-step.component.scss'],
  providers: [
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => RecipeStepComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecipeStepComponent extends NodeViewComponent<RecipeStepAttrs> {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    super();

    effect((onCleanup) => {
      const editor = this.editor();
      const handler = ({ transaction }: EditorEvents['transaction']) => {
        if (transaction.docChanged) {
          this.docVersion.update((v) => v + 1);
        }
      };
      editor.on('transaction', handler);
      onCleanup(() => editor.off('transaction', handler));
    });

    effect(() => {
      const element = this.titleElement()?.nativeElement;
      if (element && this.document.activeElement !== element) {
        element.textContent = this.stepTitle() ?? null;
      }
    });
  }

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly titleElement = viewChild<ElementRef<HTMLSpanElement>>('titleElement');

  readonly isEditable = computed(() => this.editor().isEditable);
  readonly stepTitle = this.getProp('title');
  readonly katexOptions: KatexOptions = { throwOnError: false, strict: 'warn' };
  readonly titleEditing = signal(false);
  readonly titleHasMarkdown = computed(() => /[$\\]/.test(this.stepTitle() || ''));

  private readonly docVersion = signal(0);

  readonly stepIndex = computed(() => {
    this.docVersion();
    const editor = this.editor();
    const pos = this.getPos()();
    const resolvedPos = editor.state.doc.resolve(pos);
    return resolvedPos.index() + 1;
  });

  override getDefaults = (): RecipeStepAttrs => ({});

  startTitleEdit(): void {
    this.titleEditing.set(true);
    this.cdr.detectChanges();
    const el = this.titleElement()?.nativeElement;
    if (el) {
      el.textContent = this.stepTitle() || '';
      el.focus();
    }
  }

  stopTitleEdit(): void {
    this.titleEditing.set(false);
  }

  onTitleInput(event: Event): void {
    const target = event.target as HTMLElement;
    const newTitle = target.textContent || '';
    this.updateProp('title', newTitle);
  }

  moveStepUp(): void {
    const editor = this.editor();
    const pos = this.getPos()();
    const node = this.node();
    const resolvedPos = editor.state.doc.resolve(pos);
    const index = resolvedPos.index();
    const parent = resolvedPos.parent;

    // First step → wrap to end
    if (index <= 0) {
      const parentEnd = resolvedPos.start() + parent.content.size;
      const { tr } = editor.state;
      const nodeSlice = tr.doc.slice(pos, pos + node.nodeSize);
      tr.delete(pos, pos + node.nodeSize);
      tr.insert(parentEnd - node.nodeSize, nodeSlice.content);
      editor.view.dispatch(tr);
      return;
    }

    let prevPos = resolvedPos.start();
    for (let i = 0; i < index - 1; i++) {
      prevPos += parent.child(i).nodeSize;
    }

    const { tr } = editor.state;
    const nodeSlice = tr.doc.slice(pos, pos + node.nodeSize);
    tr.delete(pos, pos + node.nodeSize);
    tr.insert(prevPos, nodeSlice.content);
    editor.view.dispatch(tr);
  }

  moveStepDown(): void {
    const editor = this.editor();
    const pos = this.getPos()();
    const node = this.node();
    const resolvedPos = editor.state.doc.resolve(pos);
    const index = resolvedPos.index();
    const parent = resolvedPos.parent;

    // Last step → wrap to first position
    if (index >= parent.childCount - 1) {
      const firstStepPos = resolvedPos.start();
      const { tr } = editor.state;
      const nodeSlice = tr.doc.slice(pos, pos + node.nodeSize);
      tr.delete(pos, pos + node.nodeSize);
      tr.insert(firstStepPos, nodeSlice.content);
      editor.view.dispatch(tr);
      return;
    }

    const nextNode = parent.child(index + 1);
    const nextPos = pos + node.nodeSize;

    const { tr } = editor.state;
    const nextSlice = tr.doc.slice(nextPos, nextPos + nextNode.nodeSize);
    tr.delete(nextPos, nextPos + nextNode.nodeSize);
    tr.insert(pos, nextSlice.content);
    editor.view.dispatch(tr);
  }

  removeStep(): void {
    const editor = this.editor();
    const pos = this.getPos()();
    const node = this.node();
    const resolvedPos = editor.state.doc.resolve(pos);
    const parent = resolvedPos.parent;

    (this.document.activeElement as HTMLElement)?.blur?.();

    const { tr } = editor.state;
    if (parent.childCount <= 1) {
      const parentPos = resolvedPos.before(resolvedPos.depth);
      tr.delete(parentPos, parentPos + parent.nodeSize);
    } else {
      tr.delete(pos, pos + node.nodeSize);
    }
    editor.view.dispatch(tr);
  }
}
